import { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Building, Floor, Town } from 'src/api/types';
import { FloorChipItem } from 'src/components/FloorChips/FloorChips';
import { FloorMapProvider } from 'src/components/FloorMap/FloorMapProvider';
import { Point } from 'src/components/FloorMap/types';
import { Page } from 'src/components/Page/Page';
import { useIdleTimer } from 'src/hooks/useIdleTimer';
import { useMapStyle } from 'src/hooks/useMapStyle';
import { FullscreenContainer } from 'src/providers/FullscreenProvider/FullscreenContainer/FullscreenContainer';
import { useFullscreenElement } from 'src/providers/FullscreenProvider/FullscreenProvider';
import {
  SearchBookingObjectProvider,
  useSearchBookingObject,
} from 'src/providers/SearchBookingObjectProvider';
import { SidesheetSlot } from 'src/providers/SidesheetSlot';
import { RoutePath } from 'src/routes/types';
import {
  getDeviceSize,
  getIsMobileWithTouchscreen,
} from 'src/store/agent/selectors';
import { getDeskLocation, getDesksState } from 'src/store/desks/selectors';
import {
  desksPollingStarted,
  desksPollingStopped,
  fetchDesks,
} from 'src/store/desks/services';
import { desksActions, openDesk } from 'src/store/desks/slice';
import { getLastBookingsState } from 'src/store/lastBookings/selectors';
import { navigationActions } from 'src/store/navigation/slice';
import { getSidesheetMode } from 'src/store/sidesheet/selectors';
import { setSidesheetSlots, sidesheetActions } from 'src/store/sidesheet/slice';
import { SidesheetSlotItem } from 'src/store/sidesheet/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize } from 'src/types';

import { FloorChipsProvider } from '../../components/FloorChips/FloorChipsContext';

import { DeskBookingBottomSheet } from './DeskBookingBottomSheet/DeskBookingBottomSheet';
import { DeskBookingFloorMap } from './DeskBookingFloorMap';
import classes from './DeskBookingPage.module.scss';
import { DeskBookingPageActions } from './DeskBookingPageActions/DeskBookingPageActions';
import { DeskMapSettingsBottomSheet } from './DeskMapSettingsBottomSheet/DeskMapSettingsBottomSheet';

const modalSidesheetSizes = new Set([
  DeviceSize.XXS,
  DeviceSize.XS,
  DeviceSize.S,
]);

const filterFloorItems = (item: FloorChipItem): boolean =>
  !item.value.isParking;

const PageLayout: FC = () => {
  const { fullscreen } = useFullscreenElement();
  const mode = useSelector(getSidesheetMode);
  const [fixed, mapStyle] = useMapStyle();
  const isMobile = useSelector(getIsMobileWithTouchscreen);

  return (
    <Page
      actions={<DeskBookingPageActions />}
      fullscreen={fullscreen}
      isSidesheetOpened={mode === 'standard'}
      fixed={fixed}
      testId='deskBooking'
      contentClassName={isMobile ? classes.mobile : undefined}
    >
      <div style={mapStyle}>
        <DeskBookingFloorMap />
      </div>
      {isMobile && (
        <>
          <DeskMapSettingsBottomSheet />
          <DeskBookingBottomSheet />
        </>
      )}
    </Page>
  );
};

const Inner: FC = () => {
  const { floor, loading } = useSelector(getDeskLocation);
  const { backgroundData, bookingObjects } = useSelector(getDesksState);
  const { desk } = useSelector(getLastBookingsState);

  const dispatch = useAppDispatch();

  const { searchedBookingObjectRef, setSearchedBookingObject } =
    useSearchBookingObject();

  const laskDeskId = useMemo(() => {
    if (desk && floor && desk.floor.floorId === floor.floorId) {
      return desk.placeId.toString();
    }
  }, [desk, floor]);

  const searchedDeskId = searchedBookingObjectRef.current?.id.toString();

  const offset = useMemo((): Point | undefined => {
    const offsetDeskId = searchedDeskId ?? laskDeskId;
    if (!offsetDeskId) return;

    return bookingObjects.get(offsetDeskId);
  }, [bookingObjects, laskDeskId, searchedDeskId]);

  useEffect(() => {
    if (searchedDeskId) {
      dispatch(openDesk(searchedDeskId));
    }
  }, [dispatch, searchedDeskId, setSearchedBookingObject]);

  return (
    <FloorMapProvider
      floor={floor}
      backgroundData={backgroundData}
      floorLoading={loading}
      offset={offset}
    >
      <FullscreenContainer>
        <SidesheetSlot
          id='pageLevelModal'
          mode='modal'
          hasOverlay
          elevation='above'
        >
          <SidesheetSlot id='pageLevel'>
            <PageLayout />
          </SidesheetSlot>
        </SidesheetSlot>
      </FullscreenContainer>
    </FloorMapProvider>
  );
};

export const DeskBookingPage: FC = () => {
  const size = useSelector(getDeviceSize);
  const { fullscreen } = useFullscreenElement();
  const { building, floor } = useSelector(getDeskLocation);

  const dispatch = useAppDispatch();

  const startPolling = useCallback(() => {
    dispatch(desksPollingStarted(true));
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    dispatch(desksPollingStopped());
  }, [dispatch]);

  const setTown = useCallback(
    (value: Town | undefined) => {
      if (value) dispatch(desksActions.setTown(value));
    },
    [dispatch],
  );

  const setBuilding = useCallback(
    (value: Building | undefined) => {
      if (value) dispatch(desksActions.setBuilding(value));
    },
    [dispatch],
  );

  const setFloor = useCallback(
    (value: Floor | undefined) => {
      if (value) {
        dispatch(sidesheetActions.close());
        dispatch(desksActions.setFloor(value));
      }
    },
    [dispatch],
  );

  const setFrom = useCallback(
    (date: Date | undefined) => {
      dispatch(desksActions.setDate(date?.toISOString()));
    },
    [dispatch],
  );

  useEffect(() => {
    const standardSlotId = fullscreen ? 'pageLevel' : 'tabsLevel';
    const modalSlotId = fullscreen ? 'pageLevelModal' : 'tabsLevelModal';
    const standardSlot: SidesheetSlotItem = {
      id: standardSlotId,
      mode: 'standard',
    };
    const modalSlot: SidesheetSlotItem = {
      id: modalSlotId,
      mode: 'modal',
    };
    const isModalSlot = modalSidesheetSizes.has(size);

    dispatch(
      setSidesheetSlots([
        {
          sidesheetId: 'deskBooking',
          slot: isModalSlot ? modalSlot : standardSlot,
        },
        {
          sidesheetId: 'deskOffice',
          slot: modalSlot,
        },
        {
          sidesheetId: 'desksFilters',
          slot: isModalSlot ? modalSlot : standardSlot,
        },
      ]),
    );
    dispatch(
      navigationActions.setSegments([RoutePath.Main, RoutePath.DeskBooking]),
    );
  }, [dispatch, fullscreen, size]);

  const init = useCallback(async () => {
    if (floor?.floorId) {
      await dispatch(desksPollingStopped());
      await dispatch(fetchDesks(true));
      await dispatch(desksPollingStarted(false));
    }
  }, [dispatch, floor?.floorId]);

  useEffect(() => {
    init();
  }, [init]);

  useIdleTimer({
    idleMs: 60000,
    onPause: stopPolling,
    onResume: startPolling,
  });

  return (
    <SearchBookingObjectProvider
      setTown={setTown}
      setBuilding={setBuilding}
      setFloor={setFloor}
      setTimeRange={setFrom}
    >
      <FloorChipsProvider
        floor={floor}
        setFloor={setFloor}
        buildingId={building?.buildingId}
        filterItems={filterFloorItems}
      >
        <Inner />
      </FloorChipsProvider>
    </SearchBookingObjectProvider>
  );
};

export default DeskBookingPage;
